import { z } from 'zod';
import { civilDate, idSchema, isoDateTime } from './common.js';
export const regionKindSchema = z.enum(['continent', 'country', 'city', 'district']);
export const planetSchema = z.object({
    id: idSchema,
    name: z.string(),
    slug: z.string(),
    artPlanetKey: z.string(),
    orbitIndex: z.number().int(),
    orbitRadius: z.number(),
    importanceWeight: z.number().positive(),
    colourSeed: z.number().int(),
    archived: z.boolean(),
    /** Aggregate of the whole planet, for the orbital view. */
    health: z.number().min(0).max(1),
    openTasks: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    /**
     * Its worst neglected building, how many are slipping or acute, and the
     * worst few by name: what the planet's aura is showing from the system view.
     */
    neglect: z.object({
        worst: z.number().min(0).max(1),
        smouldering: z.number().int().nonnegative(),
        burning: z.number().int().nonnegative(),
        troubles: z
            .array(z.object({
            name: z.string(),
            kind: z.enum(['floors', 'salah', 'person', 'faculty', 'forest', 'reading', 'book', 'strength']),
            unit: z.string(),
            weathering: z.number().min(0).max(1),
            quietDays: z.number().int().nullable(),
            /** A prayer's rating out of five. */
            rating: z.number().nullable(),
        })),
    }),
});
export const planetListSchema = z.object({
    universe: z.object({ id: idSchema, name: z.string() }),
    planets: z.array(planetSchema),
});
export const regionNodeSchema = z.object({
    id: idSchema,
    parentId: idSchema.nullable(),
    kind: regionKindSchema,
    name: z.string(),
    slug: z.string(),
    depth: z.number().int().nonnegative(),
    sortIndex: z.number().int(),
    importanceWeight: z.number().positive(),
    /** Share of the parent's area; derived, never stored. */
    share: z.number().min(0).max(1),
    artSlotKey: z.string().nullable(),
    /** Which of a place's possible shapes it takes; 0 is the original. */
    geometrySeed: z.number().int(),
    /** A continent's size against its authored coastline. */
    geometryScale: z.number().min(0.4).max(1.6),
    reviewCadenceDays: z.number().int().positive().nullable(),
    briefMd: z.string().nullable(),
    version: z.number().int(),
    openTasks: z.number().int().nonnegative(),
    overdueTasks: z.number().int().nonnegative(),
    lastActivityAt: isoDateTime.nullable(),
    children: z.array(z.string()),
    health: z.object({
        deadline: z.number().min(0).max(1),
        neglect: z.number().min(0).max(1),
        combined: z.number().min(0).max(1),
    }),
    urgency: z.enum(['none', 'soon', 'urgent', 'critical']),
});
export const regionTreeSchema = z.object({
    planet: planetSchema,
    /** Region ids at the top of this planet, in order. */
    roots: z.array(idSchema),
    regions: z.record(regionNodeSchema),
});
export const createRegionSchema = z.object({
    planetId: idSchema,
    parentId: idSchema.nullable().optional(),
    kind: regionKindSchema,
    name: z.string().min(1).max(120),
    importanceWeight: z.number().positive().max(1000).optional(),
    reviewCadenceDays: z.number().int().positive().max(3650).nullable().optional(),
    artSlotKey: z.string().max(120).nullable().optional(),
});
export const updateRegionSchema = z.object({
    version: z.number().int(),
    name: z.string().min(1).max(120).optional(),
    briefMd: z.string().max(20000).nullable().optional(),
    reviewCadenceDays: z.number().int().positive().max(3650).nullable().optional(),
    artSlotKey: z.string().max(120).nullable().optional(),
    geometrySeed: z.number().int().min(0).max(2_000_000_000).optional(),
    geometryScale: z.number().min(0.4).max(1.6).optional(),
});
export const moveRegionSchema = z.object({
    parentId: idSchema.nullable(),
    planetId: idSchema.optional(),
});
export const reweightSchema = z.object({
    weights: z.array(z.object({ id: idSchema, weight: z.number().positive().max(1000) })).min(1).max(64),
});
export const taskStatusSchema = z.enum(['open', 'done', 'dropped']);
/**
 * Three tiers, and 'critical' is kept only because the enum in the database
 * still carries it: nothing writes it any more. 'none' is whenever, 'soon' is
 * this week, 'urgent' is now.
 */
export const taskUrgencySchema = z.enum(['none', 'soon', 'urgent', 'critical']);
/** How much it matters, said the way it gets said out loud: P1, P2, P3. */
export const taskImportanceSchema = z.enum(['p1', 'p2', 'p3']);
export const taskSchema = z.object({
    id: idSchema,
    regionId: idSchema.nullable(),
    /** The planet it is closest to: kept for the record, and for where it sails. */
    planetId: idSchema.nullable(),
    seriesId: idSchema.nullable(),
    title: z.string(),
    bodyMd: z.string().nullable(),
    status: taskStatusSchema,
    targetDate: civilDate.nullable(),
    hardDue: z.boolean(),
    urgency: taskUrgencySchema,
    importance: taskImportanceSchema,
    effortMinutes: z.number().int().positive().nullable(),
    sortIndex: z.number().int(),
    version: z.number().int(),
    createdAt: isoDateTime,
    completedAt: isoDateTime.nullable(),
});
export const taskListSchema = z.object({ tasks: z.array(taskSchema), nextCursor: z.string().nullable() });
export const createTaskSchema = z.object({
    regionId: idSchema.nullable().optional(),
    planetId: idSchema.nullable().optional(),
    title: z.string().min(1).max(500),
    bodyMd: z.string().max(50000).nullable().optional(),
    targetDate: civilDate.nullable().optional(),
    hardDue: z.boolean().optional(),
    urgency: taskUrgencySchema.optional(),
    importance: taskImportanceSchema.optional(),
    effortMinutes: z.number().int().positive().max(10000).nullable().optional(),
});
export const updateTaskSchema = createTaskSchema.partial().extend({ version: z.number().int() });
export const setStatusSchema = z.object({ status: taskStatusSchema });
/** One entry on a task's timeline. Written once, never edited. */
export const taskNoteSchema = z.object({
    id: idSchema,
    taskId: idSchema,
    bodyMd: z.string(),
    createdAt: isoDateTime,
});
export const taskNoteListSchema = z.object({ notes: z.array(taskNoteSchema) });
export const createTaskNoteSchema = z.object({ bodyMd: z.string().min(1).max(20000) });
/** MAP-058: one call from raw text to a filed, dated task. */
export const captureSchema = z.object({
    text: z.string().min(1).max(500),
    /** Overrides whatever the text implies. */
    regionId: idSchema.nullable().optional(),
});
/**
 * MAP-145: what the parser makes of the text so far, for the preview under the
 * capture field. The spans are returned so the client can point at the words
 * that did the work rather than just reporting a conclusion.
 */
export const capturePreviewSchema = z.object({
    // The day the parser reckoned from — a CivilDate, which is the parser's own
    // {year, month, day}, not the YYYY-MM-DD string the wire uses elsewhere.
    today: z.object({ year: z.number().int(), month: z.number().int(), day: z.number().int() }),
    date: z
        .object({ date: civilDate, text: z.string(), start: z.number(), end: z.number() })
        .nullable(),
    title: z.string(),
    regions: z.array(z.object({
        id: idSchema,
        name: z.string(),
        confidence: z.number(),
        start: z.number(),
        end: z.number(),
        explicit: z.boolean(),
    })),
});
export const captureResultSchema = z.object({
    task: taskSchema,
    interpretation: z.object({
        title: z.string(),
        date: z
            .object({ value: civilDate, phrase: z.string(), start: z.number(), end: z.number() })
            .nullable(),
        region: z.object({ id: idSchema, name: z.string(), confidence: z.number() }).nullable(),
        alternatives: z.array(z.object({ id: idSchema, name: z.string(), confidence: z.number() })),
    }),
});
export const inboxCountSchema = z.object({ count: z.number().int().nonnegative() });
export const activityKindSchema = z.enum([
    'created', 'completed', 'reopened', 'dropped', 'edited',
    'reviewed', 'moved', 'reweighted', 'archived', 'unarchived', 'progressed', 'prayed',
]);
export const activityEntrySchema = z.object({
    id: z.string(),
    kind: activityKindSchema,
    regionId: idSchema.nullable(),
    regionName: z.string().nullable(),
    taskId: idSchema.nullable(),
    taskTitle: z.string().nullable(),
    occurredAt: isoDateTime,
    meta: z.record(z.unknown()),
});
export const activityFeedSchema = z.object({ entries: z.array(activityEntrySchema) });
export const healthReasonSchema = z.object({
    kind: z.enum(['task', 'neglect', 'child']),
    label: z.string(),
    detail: z.string(),
    score: z.number().min(0).max(1),
    taskId: idSchema.nullable(),
    regionId: idSchema.nullable(),
});
export const healthExplanationSchema = z.object({
    regionId: idSchema,
    combined: z.number().min(0).max(1),
    reasons: z.array(healthReasonSchema),
});
/** Mutations answer with just enough to update the client without a refetch. */
export const createdSchema = z.object({ id: idSchema, version: z.number().int().optional() });
export const reweightResultSchema = z.object({ shares: z.record(z.number().min(0).max(1)) });
export const archiveResultSchema = z.object({ affected: z.array(idSchema) });
/**
 * A project, as a building. Floors only ever go up — what is built is built —
 * so the pressure lives in `lastProgressAt`: a site nobody has visited weathers
 * while keeping every floor it has.
 */
export const buildingSchema = z.object({
    id: idSchema,
    regionId: idSchema,
    name: z.string(),
    /** What one floor is: a chapter, a session, a juz. */
    unit: z.string(),
    floorsTotal: z.number().int().positive(),
    floorsDone: z.number().int().nonnegative(),
    /** How often you mean to show up, in days. Null means no rhythm to keep. */
    rhythmDays: z.number().int().positive().nullable(),
    lastProgressAt: isoDateTime.nullable(),
    completedAt: isoDateTime.nullable(),
    /**
     * 'floors' is built a storey at a time; 'salah' is rated daily instead;
     * 'person' is someone from Ilm, drawn as a figure (see `person`);
     * 'faculty' is one of Ilm's faculties, drawn as rain and growth (see `faculty`);
     * 'forest' is Arabic's mastered words, a tree each (see `forest`);
     * 'reading' is Arabic's reading comprehension, drawn as an oasis (see `reading`);
     * 'book' is a book read, drawn as an award medal (see `book`);
     * 'strength' is strength of self, drawn as a meditating figure (see `strength`).
     */
    kind: z.enum(['floors', 'salah', 'person', 'faculty', 'forest', 'reading', 'book', 'strength']),
    prayer: z.enum(['fajr', 'dhuhr', 'asr', 'maghrib', 'isha']).nullable(),
    /** A salah building's rating out of five; null for everything else. */
    rating: z.number().min(0).max(5).nullable(),
    /** Tajwid's marks on a surah: gold star, order known, flagged to revise. */
    marks: z.array(z.enum(['star', 'order', 'flagged'])),
    /** For a person: who they are in Ilm, and whether this is the Prophet ﷺ, drawn as light. */
    person: z.object({ ilmId: z.number().int(), prophet: z.boolean() }).nullable(),
    /**
     * For a faculty: how much of its quiz is held. `score` is the share of its
     * questions whose latest answer was right; unanswered ones count as not known.
     */
    faculty: z
        .object({
        ilmId: z.number().int(),
        score: z.number().min(0).max(1),
        questions: z.number().int().nonnegative(),
        answered: z.number().int().nonnegative(),
        correct: z.number().int().nonnegative(),
    })
        .nullable(),
    /** For a forest: every word mastered in Arabic, oldest first. Sentences are fetched per word. */
    forest: z
        .object({
        words: z.array(z.object({
            id: z.number().int(),
            lemma: z.string(),
            meaning: z.string(),
            kind: z.enum(['verb', 'noun', 'adjective', 'particle', 'name']),
            masteredAt: z.string(),
            /** False once it has been forgotten since (tapped in a text, or answered wrong). */
            known: z.boolean(),
            weathering: z.number().min(0).max(1),
        })),
    })
        .nullable(),
    /** For reading: recent comprehension (0..1, newest sittings weighted most) and speed. */
    reading: z
        .object({
        score: z.number().min(0).max(1),
        wpm: z.number().nullable(),
        sittings: z.number().int().nonnegative(),
        lastReadAt: z.string().nullable(),
        recent: z.array(z.object({ title: z.string(), comprehension: z.number(), wpm: z.number().nullable(), at: z.string() })),
    })
        .nullable(),
    /**
     * For a book: who wrote it and how it was rated (whole stars 1–5, which pick
     * the medal's metal). `readAt` is when it was finished if Goodreads knows,
     * else null; `order` is its place in finishing order, oldest first, for which
     * the date added stands in when there is no read date. The review is fetched
     * per book.
     */
    book: z
        .object({
        author: z.string(),
        rating: z.number().int().min(0).max(5),
        readAt: z.string().nullable(),
        addedAt: z.string(),
        order: z.number().int().nonnegative(),
    })
        .nullable(),
    /**
     * For strength of self: the day it began (day 1), every day it was reset
     * (day 0), and the count on the day asked about — which is what turns the
     * figure gold, wholly so from day 40.
     */
    strength: z
        .object({
        began: z.string(),
        resets: z.array(z.string()),
        count: z.number().int().nonnegative(),
        best: z.number().int().nonnegative(),
    })
        .nullable(),
    /** Days since the last floor, or null if nothing has been built yet. */
    quietDays: z.number().int().nonnegative().nullable(),
    /** 0 = working, 1 = long abandoned. Drives the weeds, never the floors. */
    weathering: z.number().min(0).max(1),
});
export const arabicWordSchema = z.object({
    word: z.object({
        id: z.number().int(),
        lemma: z.string(),
        meaning: z.string(),
        root: z.string(),
        kind: z.string(),
        masteredAt: z.string(),
        known: z.boolean(),
        lastAnswerAt: z.string().nullable(),
        sentences: z.array(z.object({ text: z.string(), form: z.string() })),
    }),
});
export const buildingListSchema = z.object({ buildings: z.array(buildingSchema) });
export const createBuildingSchema = z.object({
    regionId: idSchema,
    name: z.string().min(1).max(200),
    unit: z.string().min(1).max(40).optional(),
    floorsTotal: z.number().int().positive().max(500),
    rhythmDays: z.number().int().positive().max(365).nullable().optional(),
});
export const addFloorsSchema = z.object({ floors: z.number().int().min(1).max(100) });
export const universeNameSchema = z.object({ id: idSchema, name: z.string() });
export const movedRegionSchema = z.object({
    id: idSchema,
    parentId: idSchema.nullable(),
    planetId: idSchema,
});
/** What Ilm knows about a person, shown in the flyout. Fetched on demand, never part of the world. */
const ilmRef = z.object({ title: z.string(), url: z.string() });
export const ilmPersonSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    nameAr: z.string(),
    kunya: z.string(),
    aliases: z.array(z.string()),
    role: z.string(),
    era: z.string(),
    born: z.string(),
    diedAh: z.string(),
    diedCe: z.string(),
    region: z.string(),
    madhhab: z.string(),
    bio: z.string(),
    teachers: z.array(z.string()),
    students: z.array(z.string()),
    works: z.array(z.string()),
    verification: z.string(),
    verifyNote: z.string(),
    links: z.array(ilmRef),
    familiarity: z.number(),
    myNotes: z.string(),
    prophet: z.boolean(),
    lessons: z.array(z.object({
        id: z.number(),
        title: z.string(),
        series: z.string(),
        lesson: z.string(),
        teacher: z.string(),
        url: z.string(),
        heardAs: z.string(),
        context: z.string(),
    })),
    books: z.array(z.object({ title: z.string(), title_ar: z.string().nullable(), verification: z.string() })),
    hadiths: z.array(z.object({ text_en: z.string(), collection: z.string(), grading: z.string(), url: z.string() })),
    url: z.string(),
});
/** One of Ilm's faculties and how much of its quiz is held, as its feed sends it. */
export const ilmFacultySchema = z.object({
    id: z.number().int(),
    name: z.string(),
    nameAr: z.string(),
    questions: z.number().int().nonnegative(),
    answered: z.number().int().nonnegative(),
    correct: z.number().int().nonnegative(),
    score: z.number().min(0).max(1),
});
export const buildingPersonSchema = z.object({ person: ilmPersonSchema });
/** A book's review as written on Goodreads, shown in its card. */
export const bookReviewSchema = z.object({ review: z.string() });
/** Strength of self, for its card: the state, and the day the server calls today. */
export const strengthHistorySchema = z.object({
    buildingId: z.string(),
    began: z.string(),
    resets: z.array(z.string()),
    today: z.string(),
    count: z.number().int().nonnegative(),
    best: z.number().int().nonnegative(),
});
//# sourceMappingURL=world.js.map